import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Work as JobDetailIcon,
  Save as SaveIcon,
  PlayArrow as StartJobIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { fetchSystemJobDetail, updateSystemJob } from '../api/features/systemJob/systemJobApi';
import { SAP_COLORS, SAP_TYPOGRAPHY } from '../theme/sapDesignSystem';
import { SAPButton } from '../components/design-system';
import { epochToDateTime } from '../api/utils';

interface SystemJobDetailData {
  createdBy: number;
  creationDate: number;
  lastUpdateDate: number;
  lastUpdatedBy: number;
  clientId: number;
  id: number;
  jobName: string;
  lastStartTime: number;
  lastEndTime: number;
  nextRunTime: number;
  version: number;
  intervalType: string;
  interval: number;
  toDateTime: number | null;
  status: string;
}

const statusOptions = [
  { value: 'RUNNING', label: 'Running', color: 'success' },
  { value: 'STOPPED', label: 'Stopped', color: 'error' },
  { value: 'PAUSED', label: 'Paused', color: 'warning' },
  { value: 'COMPLETED', label: 'Completed', color: 'success' },
  { value: 'FAILED', label: 'Failed', color: 'error' },
  { value: 'SCHEDULED', label: 'Scheduled', color: 'info' },
];

export default function SystemJob() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientCode } = useClientCode();

  const [jobData, setJobData] = useState<SystemJobDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [startJobDialogOpen, setStartJobDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable fields
  const [interval, setInterval] = useState<number>(0);
  const [status, setStatus] = useState<string>('');
  const [nextRunTime, setNextRunTime] = useState<string>('');
  const [displayJobName, setDisplayJobName] = useState<string>(id ? `Job ${id}` : 'System Job');

  const fetchJobDetails = async () => {
    if (!id) return;
    const jobId = parseInt(id);
    if (isNaN(jobId)) {
      setError('Invalid Job ID format');
      return;
    }
    try {
      const data = await fetchSystemJobDetail({ clientCode, jobId });
      setJobData(data);
      setDisplayJobName(data.jobName || `Job ${id}`);
      setInterval(data.interval || 0);
      setStatus(data.status || '');
      setNextRunTime(data.nextRunTime ? new Date(data.nextRunTime * 1000).toISOString().slice(0, 16) : '');
      setError(null);
    } catch (err: any) {
      setError('Failed to load system job details');
    }
  };

  useEffect(() => {
    setLoading(true);
    if (!id) {
      setError('Job ID is required');
      setLoading(false);
      return;
    }
    fetchJobDetails().finally(() => setLoading(false));
  }, [id, clientCode]);

  const handleSave = async () => {
    if (!jobData) return;
    try {
      setSaving(true);
      setError(null);

      let nextRunTimeEpoch = jobData.nextRunTime;
      if (nextRunTime && nextRunTime !== new Date(jobData.nextRunTime * 1000).toISOString().slice(0, 16)) {
        const date = new Date(nextRunTime);
        if (!isNaN(date.getTime())) {
          nextRunTimeEpoch = Math.floor(date.getTime() / 1000);
        }
      }

      const payload = { ...jobData, interval, status, nextRunTime: nextRunTimeEpoch };

      await updateSystemJob({ clientCode, jobId: jobData.id, payload });

      toast.success('System job updated successfully');

      const updatedData = await fetchSystemJobDetail({ clientCode, jobId: parseInt(id!) });
      setJobData(updatedData);
      setInterval(updatedData.interval || 0);
      setStatus(updatedData.status || '');
      setNextRunTime(updatedData.nextRunTime ? new Date(updatedData.nextRunTime * 1000).toISOString().slice(0, 16) : '');
    } catch (err: any) {
      setError('Failed to update system job');
      toast.error('Failed to update system job');
    } finally {
      setSaving(false);
    }
  };

  const handleStartJobClick = () => {
    setStartJobDialogOpen(true);
  };

  const handleStartJobConfirm = () => {
    setStartJobDialogOpen(false);
    toast.success('Job start requested');
  };

  const handleStartJobCancel = () => {
    setStartJobDialogOpen(false);
  };

  const handleBreadcrumbBack = () => {
    const fromListSearch = (location.state as { fromListSearch?: string })?.fromListSearch;
    if (fromListSearch) {
      navigate(fromListSearch);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/system-job-detail');
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: SAP_COLORS.sapGray1,
      '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
    },
    '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
    '& .MuiInputBase-input': { color: SAP_COLORS.sapGray6 },
  };

  const editableFieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: SAP_COLORS.sapWhite,
      '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
      '&:hover fieldset': { borderColor: SAP_COLORS.sapBlue },
      '&.Mui-focused fieldset': { borderColor: SAP_COLORS.sapBlue },
    },
    '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
    '& .MuiInputBase-input': { color: SAP_COLORS.sapGray7 },
  };

  // Loading state
  if (loading && !jobData) {
    return (
      <Box sx={{ backgroundColor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          sx={{
            backgroundColor: SAP_COLORS.sapWhite,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            m: 3,
          }}
        >
          <CircularProgress size={60} sx={{ color: SAP_COLORS.sapBlue, mb: 3 }} />
          <Typography variant="h6" sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium }}>
            Loading system job details...
          </Typography>
          <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray5, mt: 1 }}>
            Fetching information for Job ID: {id}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state (no data)
  if (error && !jobData) {
    return (
      <Box sx={{ backgroundColor: SAP_COLORS.sapGray1, minHeight: '100vh', p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <SAPButton variant="secondary" onClick={() => navigate('/system-job-detail')}>
          Back to System Jobs
        </SAPButton>
      </Box>
    );
  }

  if (!jobData) {
    return (
      <Box sx={{ backgroundColor: SAP_COLORS.sapGray1, minHeight: '100vh', p: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          No system job data found
        </Alert>
        <SAPButton variant="secondary" onClick={() => navigate('/system-job-detail')}>
          Back to System Jobs
        </SAPButton>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
      {/* Top Bar: Breadcrumb + Actions - matches JobDetailEdit */}
      <Box
        sx={{
          backgroundColor: SAP_COLORS.sapWhite,
          color: SAP_COLORS.sapGray7,
          px: 3,
          py: 2,
          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Breadcrumbs
              separator="›"
              sx={{
                color: SAP_COLORS.sapGray7,
                '& .MuiBreadcrumbs-separator': { color: SAP_COLORS.sapGray5 },
              }}
            >
              <Link
                component="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleBreadcrumbBack();
                }}
                sx={{
                  color: SAP_COLORS.sapBlue,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  font: 'inherit',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                System Jobs
              </Link>
              <Typography
                color={SAP_COLORS.sapGray7}
                sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}
              >
                {displayJobName}
              </Typography>
            </Breadcrumbs>
          </Box>

          <Box display="flex" gap={2}>
            <SAPButton
              variant="success"
              startIcon={<StartJobIcon />}
              onClick={handleStartJobClick}
            >
              Start Job
            </SAPButton>
          </Box>
        </Box>
      </Box>

      {/* Tabs Section - single tab for consistency with Job Detail */}
      <Box
        sx={{
          backgroundColor: SAP_COLORS.sapWhite,
          px: { xs: 2, sm: 3 },
          position: 'sticky',
          top: 64,
          zIndex: 9,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <Tabs
          value={0}
          variant="fullWidth"
          sx={{
            minHeight: { xs: 44, sm: 48 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: { xs: 44, sm: 48 },
              color: SAP_COLORS.sapGray5,
              flex: 1,
              minWidth: 0,
              '&.Mui-selected': { color: SAP_COLORS.sapBlue },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: SAP_COLORS.sapBlue,
              height: 3,
            },
          }}
        >
          <Tab label="Job Detail" icon={<JobDetailIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Main Content Area - matches JobDetailEdit */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          mt: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 'calc(100vh - 220px)',
        }}
      >
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            backgroundColor: SAP_COLORS.sapWhite,
            height: '100%',
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              flex: 1,
              overflow: 'auto',
            }}
          >
            {/* Read-only top row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                  Job Name
                </Typography>
                <TextField value={jobData.jobName} disabled fullWidth size="small" sx={fieldSx} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                  Last Start Time
                </Typography>
                <TextField value={epochToDateTime(jobData.lastStartTime, true)} disabled fullWidth size="small" sx={fieldSx} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                  Last End Time
                </Typography>
                <TextField value={epochToDateTime(jobData.lastEndTime, true)} disabled fullWidth size="small" sx={fieldSx} />
              </Box>
            </Box>

            {/* Editable fields grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: { xs: 3, md: 6 },
                rowGap: 3,
              }}
            >
              <Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                  Interval Type
                </Typography>
                <TextField value={jobData.intervalType} disabled fullWidth size="small" sx={fieldSx} />
              </Box>

              <Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                  Interval
                </Typography>
                <TextField
                  type="number"
                  value={interval}
                  onChange={(e) => setInterval(parseInt(e.target.value) || 0)}
                  fullWidth
                  size="small"
                  sx={editableFieldSx}
                />
              </Box>

              <Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                  Status
                </Typography>
                <FormControl fullWidth size="small" sx={{ ...editableFieldSx, '& .MuiOutlinedInput-root': { bgcolor: SAP_COLORS.sapWhite } }}>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: SAP_COLORS.sapGray3 },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: SAP_COLORS.sapBlue },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: SAP_COLORS.sapBlue },
                    }}
                  >
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}>
                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                  Next Run Time
                </Typography>
                <TextField
                  type="datetime-local"
                  value={nextRunTime}
                  onChange={(e) => setNextRunTime(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={editableFieldSx}
                />
              </Box>

              <Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                  Version
                </Typography>
                <TextField value={jobData.version} disabled fullWidth size="small" sx={fieldSx} />
              </Box>

              <Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                  Client ID
                </Typography>
                <TextField value={jobData.clientId} disabled fullWidth size="small" sx={fieldSx} />
              </Box>
            </Box>

            {/* Save / Cancel at bottom - matches Job Detail tab pattern */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end',
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
              }}
            >
              <Button
                onClick={handleBreadcrumbBack}
                sx={{
                  color: SAP_COLORS.sapGray5,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }}
              >
                Cancel
              </Button>
              <SAPButton variant="primary" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
                {saving ? 'Saving...' : 'Save'}
              </SAPButton>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Start Job Confirmation Dialog */}
      <Dialog open={startJobDialogOpen} onClose={handleStartJobCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: SAP_COLORS.sapBlue, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>
          Confirm Job Start
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
            Do you want to run this job <strong>&quot;{displayJobName}&quot;</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleStartJobCancel}
            sx={{ color: SAP_COLORS.sapGray5, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium }}
          >
            Cancel
          </Button>
          <SAPButton variant="success" onClick={handleStartJobConfirm} startIcon={<StartJobIcon />}>
            Start Job
          </SAPButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
